/**
 * Format a date string in the given IANA timezone.
 * Falls back to browser local time if the timezone is invalid.
 */
export function formatDateInTz(dateStr: string, tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr))
  } catch {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
}

/** Numeric session date in the given timezone: "7/30/2026". */
export function formatDateNumericInTz(dateStr: string, tz?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...opts,
      ...(tz ? { timeZone: tz } : {}),
    }).format(new Date(dateStr))
  } catch {
    return new Date(dateStr).toLocaleDateString('en-US', opts)
  }
}

/**
 * Format a session date AND time in one string: "Thu, Jul 30, 2026 · 07:00 PM".
 *
 * Use this for anything that shows *when a session happens*. Calling
 * `toLocaleDateString`/`toLocaleTimeString` without a `timeZone` renders in the
 * viewer's browser timezone, which produced a real bug: the interviewer
 * dashboard (which passes the profile timezone) showed a session at 9:30 AM
 * while the evaluation page for the same session showed 7:00 PM — the same
 * 13:30 UTC booking rendered in America/New_York vs the viewer's Asia/Kolkata.
 *
 * `tz` is optional: when it is not yet known (profile still loading) this falls
 * back to browser-local rather than throwing, so a slow profile fetch degrades
 * instead of blanking the field.
 */
export function formatDateTimeInTz(dateStr: string, tz?: string): string {
  const date = new Date(dateStr)
  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  try {
    const tzOpts = tz ? { timeZone: tz } : {}
    return (
      new Intl.DateTimeFormat('en-US', { ...dateOpts, ...tzOpts }).format(date) +
      ' · ' +
      new Intl.DateTimeFormat('en-US', { ...timeOpts, ...tzOpts }).format(date)
    )
  } catch {
    // Invalid IANA name — never let a bad setting break the page.
    return (
      date.toLocaleDateString('en-US', dateOpts) +
      ' · ' +
      date.toLocaleTimeString('en-US', timeOpts)
    )
  }
}

export function formatTimeInTz(dateStr: string, tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr))
  } catch {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
}

/** Short label for the timezone, e.g. "IST", "EST", "UTC" */
export function tzAbbr(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz
  } catch {
    return tz
  }
}

/** Offset (ms) between the given IANA tz and UTC at a specific instant. */
function tzOffsetMs(utcMs: number, tz: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(utcMs))
    const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0)
    const asIfUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second'))
    return asIfUtc - utcMs
  } catch {
    return 0
  }
}

/**
 * Convert a UTC ISO timestamp to a `datetime-local` input value
 * ("YYYY-MM-DDTHH:mm") expressed as wall-clock time in the given IANA tz.
 * Used to prefill reschedule pickers with the session's current time.
 */
export function utcIsoToLocalInput(iso: string, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(iso))
    const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
    return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`
  } catch {
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
}

/**
 * Reformat a plain calendar date ("2026-09-23") as US-style "09-23-2026".
 *
 * Deliberately string-only: these values are calendar dates, not instants, so
 * `new Date('2026-09-23')` (UTC midnight) then formatting in a negative-offset
 * timezone like America/New_York would render the PREVIOUS day. Anything that is
 * not a YYYY-MM-DD date is returned untouched rather than mangled.
 */
export function formatCalendarDateUS(dateOnly?: string | null): string {
  if (!dateOnly) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly.trim())
  if (!m) return dateOnly
  const [, y, mo, d] = m
  return `${mo}-${d}-${y}`
}

/**
 * Interpret a `datetime-local` value ("YYYY-MM-DDTHH:mm") as wall-clock time in
 * the given IANA tz and return the corresponding UTC ISO instant. This is the
 * inverse of utcIsoToLocalInput — the admin picks a time "in the displayed tz"
 * and we hand the backend a proper UTC timestamp.
 */
export function localInputToUtcIso(localValue: string, tz: string): string {
  const [datePart, timePart] = localValue.split('T')
  const [y, mo, d] = datePart.split('-').map(Number)
  const [h, mi] = (timePart || '00:00').split(':').map(Number)
  const guess = Date.UTC(y, (mo || 1) - 1, d || 1, h || 0, mi || 0)
  // Two-pass refinement settles DST boundaries.
  const offset = tzOffsetMs(guess, tz)
  const refined = tzOffsetMs(guess - offset, tz)
  return new Date(guess - refined).toISOString()
}
